import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const Totals = ({ s }) => (
  <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-5" data-testid="tds-totals">
    {[["DP TDS", s.dp_tds], ["Franchise TDS", s.franchise_tds], ["Total TDS Deducted", s.total_tds], ["TDS Deposited", s.deposited], ["Pending Deposit", s.pending]].map(([l, v]) => (
      <div key={l} className="rounded-xl border border-border bg-card p-3"><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{l}</p><p className="font-head text-lg font-bold tabular">{inr(v)}</p></div>
    ))}
  </div>
);

const DS = { Pending: "text-amber-500", Deposited: "text-emerald-500", "Not Applicable": "text-muted-foreground" };

export function LedgerTable({ category, depositStatus, rows: given, testId = "tds-ledger" }) {
  const { can } = useAuth();
  const [data, setData] = useState({ rows: [], summary: null });
  const [q, setQ] = useState("");
  const [dep, setDep] = useState(null);
  const load = useCallback(() => {
    if (given) return;
    api.get("/tax/ledger", { params: { category, deposit_status: depositStatus, q: q || undefined } }).then(({ data: d }) => setData(d)).catch(() => {});
  }, [category, depositStatus, q, given]);
  useEffect(() => { load(); }, [load]);
  const rows = given || data.rows;
  return (
    <div data-testid={testId}>
      {data.summary && !given && <Totals s={data.summary} />}
      {!given && <Input className="mb-3 max-w-xs" placeholder="Search payee, FE ID, PAN, ref" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`${testId}-search`} />}
      <div className="overflow-x-auto rounded-xl border border-border bg-card">
        <Table>
          <TableHeader><TableRow className="hover:bg-transparent">
            {["Payee", "FE ID / Referral", "PAN", "Payment", "Date", "FY", "Gross", "Taxable", "TDS %", "TDS", "Net Paid", "Deposit", "Challan", ""].map((h) => <TableHead key={h}>{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={14} className="text-center text-muted-foreground">No TDS entries yet</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`ledger-row-${r.payment_ref}`}>
                <TableCell><p className="font-medium">{r.payee_name}</p><p className="text-[11px] text-muted-foreground">{r.category === "dp" ? "Delivery Partner" : "Franchise"} · {r.section}</p></TableCell>
                <TableCell className="font-mono text-xs">{r.ref_code}</TableCell>
                <TableCell className="font-mono text-xs">{r.pan || "—"}</TableCell>
                <TableCell className="font-mono text-xs">{r.payment_ref}</TableCell>
                <TableCell className="text-xs">{r.payment_date}</TableCell>
                <TableCell className="text-xs">{r.fy}</TableCell>
                <TableCell className="tabular">{inr(r.gross)}</TableCell>
                <TableCell className="tabular">{inr(r.taxable)}</TableCell>
                <TableCell className="tabular">{r.rate}%</TableCell>
                <TableCell className="tabular font-semibold text-rose-500">{inr(r.tds_amount)}</TableCell>
                <TableCell className="tabular text-emerald-500">{inr(r.net)}</TableCell>
                <TableCell className={cn("text-xs font-semibold", DS[r.deposit_status])}>{r.deposit_status}</TableCell>
                <TableCell className="text-xs">{r.challan_ref || "—"}</TableCell>
                <TableCell>{r.deposit_status === "Pending" && can("tax.deposit") && !given &&
                  <Button size="sm" variant="outline" onClick={() => setDep(r)} data-testid={`record-deposit-${r.payment_ref}`}>Record Deposit</Button>}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <DepositDialog entry={dep} onClose={() => setDep(null)} onSaved={load} />
    </div>
  );
}

function DepositDialog({ entry, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => setF({ challan_ref: "", bsr_code: "", deposit_date: new Date().toISOString().slice(0, 10) }), [entry]);
  const save = () => api.post(`/tax/ledger/${entry.id}/deposit`, f).then(() => { toast.success("TDS deposit recorded"); onSaved(); onClose(); })
    .catch((e) => toast.error(formatApiError(e?.response?.data?.detail)));
  return (
    <Dialog open={!!entry} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-md" data-testid="deposit-dialog">
        <DialogHeader><DialogTitle className="font-head">Record TDS Deposit · {inr(entry?.tds_amount)}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div><Label>Challan Reference (CIN) *</Label><Input value={f.challan_ref || ""} onChange={(e) => setF({ ...f, challan_ref: e.target.value })} data-testid="deposit-challan" /></div>
          <div><Label>BSR Code</Label><Input value={f.bsr_code || ""} onChange={(e) => setF({ ...f, bsr_code: e.target.value })} data-testid="deposit-bsr" /></div>
          <div><Label>Deposit Date</Label><Input type="date" value={f.deposit_date || ""} onChange={(e) => setF({ ...f, deposit_date: e.target.value })} data-testid="deposit-date" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button><Button onClick={save} data-testid="deposit-save">Save Deposit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
